<script setup>
import { computed, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ total_count: 0, breakdown: [] })
  },
  rowData: {
    type: Object,
    default: () => ({})
  },
  // The selector value to open Event Search on. Falls back to the row's ip for
  // the IP table; set explicitly for other selector types.
  value: {
    type: String,
    default: ''
  },
  // Explicit Event Search query string. When set, it's used verbatim instead of
  // `value` — e.g. a URL must open as `host:… AND uri:…` (the whole URL is never
  // in one field), matching how the observations enricher counts it.
  query: {
    type: String,
    default: ''
  },
  pending: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()

let dropdownEl = null
let activeBtnEl = null

const ip = computed(() => {
  return props.value || props.rowData?.ip || props.rowData?.ips?.[0]?.ip || props.rowData?.ips?.[0] || ''
})

function formatCount(n) {
  if (!n) return ''
  if (n >= 1_000_000) return '1M+'
  return n.toLocaleString()
}

const displayText = computed(() => formatCount(props.data?.total_count))

const hasData = computed(() => {
  return props.data && props.data.total_count > 0
})

const sortedBreakdown = computed(() => {
  if (!props.data?.breakdown) return []
  return [...props.data.breakdown].sort((a, b) => b.count - a.count)
})

function navigateToEventSearch(sourceName) {
  const q = props.query || ip.value
  if (!q) return
  const query = { query: q }
  if (sourceName) {
    query.sources = 's:' + sourceName
  }
  removeDropdown()
  router.push({ name: 'EventSearch', query })
}

function removeDropdown() {
  if (dropdownEl) {
    dropdownEl.remove()
    dropdownEl = null
    activeBtnEl = null
  }
  document.removeEventListener('mousedown', onClickOutside)
  window.removeEventListener('scroll', removeDropdown, true)
}

function onClickOutside(e) {
  if (activeBtnEl && activeBtnEl.contains(e.target)) return
  if (dropdownEl && dropdownEl.contains(e.target)) return
  removeDropdown()
}

function toggleDropdown(e) {
  if (dropdownEl) {
    removeDropdown()
    return
  }

  const items = sortedBreakdown.value
  const btn = e.currentTarget
  if (!btn) return

  // If no breakdown items, just navigate directly
  if (items.length === 0) {
    navigateToEventSearch(null)
    return
  }

  activeBtnEl = btn
  const rect = btn.getBoundingClientRect()

  dropdownEl = document.createElement('div')
  dropdownEl.className = 'obs-dropdown'
  dropdownEl.style.cssText = `
    position: fixed;
    top: ${rect.bottom + 4}px;
    left: ${rect.right}px;
    transform: translateX(-100%);
    z-index: 9999;
  `

  for (const item of items) {
    const row = document.createElement('div')
    row.className = 'obs-dropdown-row'

    const name = document.createElement('span')
    name.className = 'obs-dropdown-name'
    name.textContent = item.display_name

    const count = document.createElement('span')
    count.className = 'obs-dropdown-count'
    count.textContent = item.count.toLocaleString()

    row.appendChild(name)
    row.appendChild(count)
    row.addEventListener('click', () => navigateToEventSearch(item.name))
    dropdownEl.appendChild(row)
  }

  // Divider
  const divider = document.createElement('div')
  divider.className = 'obs-dropdown-divider'
  dropdownEl.appendChild(divider)

  // View all button
  const viewAll = document.createElement('div')
  viewAll.className = 'obs-dropdown-row obs-dropdown-viewall'
  viewAll.textContent = 'View all'
  viewAll.addEventListener('click', () => navigateToEventSearch(null))
  dropdownEl.appendChild(viewAll)

  document.body.appendChild(dropdownEl)
  setTimeout(() => document.addEventListener('mousedown', onClickOutside), 0)
  window.addEventListener('scroll', removeDropdown, true)
}

onBeforeUnmount(() => {
  removeDropdown()
})
</script>

<template>
  <div v-if="pending" class="obs-pending" aria-label="Loading observations">
    <span class="obs-spinner"></span>
  </div>
  <div v-else-if="hasData" class="obs-widget">
    <span class="obs-text">{{ displayText }}</span>
    <span
      class="obs-btn"
      @click="toggleDropdown"
    >
      <font-awesome-icon style="font-size: 1rem;" :icon="['fas', 'square-up-right']" />
    </span>
  </div>
</template>

<style>
.obs-dropdown {
  background: var(--surface-page);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-md);
  padding: 0.5rem 0;
  min-width: 220px;
  max-width: 320px;
  box-shadow: var(--shadow-lg);
}

.obs-dropdown-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.75rem;
  gap: 1rem;
  cursor: pointer;
}

.obs-dropdown-row:hover {
  background: rgba(255, 255, 255, 0.06);
}

.obs-dropdown-name {
  color: var(--text-primary);
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.obs-dropdown-count {
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 500;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.obs-dropdown-divider {
  height: 1px;
  background: var(--surface-overlay-3);
  margin: 0.25rem 0;
}

.obs-dropdown-viewall {
  justify-content: center;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.obs-dropdown-viewall:hover {
  color: var(--text-primary);
}
</style>

<style scoped>
.obs-widget {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.obs-text {
  white-space: nowrap;
}

.obs-btn {
  color: var(--text-disabled);
  cursor: pointer;
  flex-shrink: 0;
}

.obs-btn:hover {
  color: white;
}

.obs-pending {
  display: inline-flex;
  align-items: center;
}

.obs-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--surface-control);
  border-top-color: var(--accent);
  border-radius: var(--radius-pill);
  animation: obs-spin 1s linear infinite;
}

@keyframes obs-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
